import React, { useEffect, useMemo, useState } from "react";

// Types for the admin games panel
type GameRow = {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  image_url: string | null;
};

type GameOptionGroupRow = {
  id: number;
  key: string;
  label: string;
  sort_order: number;
  multi_select: boolean;
  is_required: boolean;
  game_id: string;
};

type GameOptionRow = {
  id: number;
  group_id: number;
  value_key: string;
  label: string;
  sort_order: number;
  is_active: boolean;
  metadata: any;
  image_url: string | null;
};

// Field component used in forms
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-xs">
      <div className="mb-1 font-semibold text-[var(--c-ink)]">{label}</div>
      {children}
    </label>
  );
}

/**
 * AdminGamesPanel component handles CRUD operations for games, option groups and options.
 */
export default function AdminGamesPanel() {
  // ---- GAMES ----
  const [games, setGames] = useState<GameRow[]>([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [savingGame, setSavingGame] = useState(false);
  const [gameError, setGameError] = useState<string | null>(null);

  const [editingGameId, setEditingGameId] = useState<string | null>(null);
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isActive, setIsActive] = useState(true);

  // ---- OPTION GROUPS ----
  const [groups, setGroups] = useState<GameOptionGroupRow[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [savingGroup, setSavingGroup] = useState(false);

  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [groupKey, setGroupKey] = useState("");
  const [groupLabel, setGroupLabel] = useState("");
  const [groupSortOrder, setGroupSortOrder] = useState("0");
  const [groupMultiSelect, setGroupMultiSelect] = useState(false);
  const [groupIsRequired, setGroupIsRequired] = useState(true);

  // ---- OPTIONS ----
  const [options, setOptions] = useState<GameOptionRow[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionError, setOptionError] = useState<string | null>(null);
  const [savingOption, setSavingOption] = useState(false);

  const [editingOptionId, setEditingOptionId] = useState<number | null>(null);
  const [optionGroupId, setOptionGroupId] = useState<number | null>(null);
  const [optionValueKey, setOptionValueKey] = useState("");
  const [optionLabel, setOptionLabel] = useState("");
  const [optionSortOrder, setOptionSortOrder] = useState("0");
  const [optionIsActive, setOptionIsActive] = useState(true);
  const [optionMetadata, setOptionMetadata] = useState("");

  // ---- OPTION IMAGE ----
  const [optionImageUrl, setOptionImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  const selectedGame = useMemo(
    () => games.find((g) => g.id === selectedGameId) || null,
    [games, selectedGameId]
  );

  const selectedGroup = useMemo(
    () =>
      optionGroupId != null
        ? groups.find((g) => g.id === optionGroupId) || null
        : null,
    [groups, optionGroupId]
  );

  function resetGameForm() {
    setEditingGameId(null);
    setCode("");
    setName("");
    setImageUrl("");
    setIsActive(true);
  }

  function hydrateGameForm(row: GameRow) {
    setEditingGameId(row.id);
    setSelectedGameId(row.id);
    setCode(row.code);
    setName(row.name);
    setImageUrl(row.image_url ?? "");
    setIsActive(row.is_active);
  }

  async function loadGames() {
    try {
      setLoadingGames(true);
      setGameError(null);
      const res = await fetch("/admin/games", {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(`Failed to load games (${res.status})`);
      }
      const data = (await res.json()) as GameRow[];
      setGames(data);

      if (data.length > 0) {
        const stillExists = selectedGameId
          ? data.some((g) => g.id === selectedGameId)
          : false;
        if (!stillExists) {
          setSelectedGameId(data[0].id);
          setEditingGroupId(null);
          setOptionGroupId(null);
        }
      } else {
        setSelectedGameId(null);
        setGroups([]);
        setOptions([]);
        setEditingGroupId(null);
        setOptionGroupId(null);
      }
    } catch (e: any) {
      setGameError(e.message || "Failed to load games");
    } finally {
      setLoadingGames(false);
    }
  }

  useEffect(() => {
    loadGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleGameSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSavingGame(true);
    setGameError(null);
    try {
      const body = {
        code: code.trim(),
        name: name.trim(),
        image_url: imageUrl.trim() || null,
        is_active: isActive,
      };

      const url = editingGameId
        ? `/admin/games/${editingGameId}`
        : "/admin/games";
      const method = editingGameId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (res.status === 409) {
        const j = await res.json().catch(() => null);
        if (j && j.error === "code_taken") {
          throw new Error("Code already in use");
        }
      }

      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `Save failed (${res.status})`);
      }

      let saved: GameRow | null = null;
      try {
        saved = (await res.json()) as GameRow;
      } catch {
        // ignore
      }

      await loadGames();

      if (saved?.id) {
        setSelectedGameId(saved.id);
      }

      if (!editingGameId) {
        resetGameForm();
      }
    } catch (e: any) {
      setGameError(e.message || "Failed to save game");
    } finally {
      setSavingGame(false);
    }
  }

  async function handleGameSoftDelete(id: string) {
    if (!window.confirm("Mark this game as inactive?")) return;
    try {
      setSavingGame(true);
      setGameError(null);
      const res = await fetch(`/admin/games/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok && res.status !== 204) {
        throw new Error(`Delete failed (${res.status})`);
      }
      await loadGames();
      if (editingGameId === id) {
        resetGameForm();
      }
      if (selectedGameId === id) {
        setSelectedGameId(null);
        setGroups([]);
        setOptions([]);
        setEditingGroupId(null);
        setOptionGroupId(null);
      }
    } catch (e: any) {
      setGameError(e.message || "Failed to deactivate game");
    } finally {
      setSavingGame(false);
    }
  }

  // ---- GROUPS / OPTIONS LOADING ----

  async function loadGroupsForGame(gameId: string) {
    try {
      setLoadingGroups(true);
      setGroupError(null);
      const res = await fetch(
        `/admin/games/${encodeURIComponent(
          gameId
        )}/option-groups`,
        { credentials: "include" }
      );
      if (!res.ok) {
        throw new Error(`Failed to load option groups (${res.status})`);
      }
      const data = (await res.json()) as GameOptionGroupRow[];
      setGroups(data);

      if (data.length > 0) {
        const stillExists =
          optionGroupId != null &&
          data.some((g) => g.id === optionGroupId);
        if (!stillExists) {
          setOptionGroupId(data[0].id);
        }
      } else {
        setEditingGroupId(null);
        setOptionGroupId(null);
        setOptions([]);
      }
    } catch (e: any) {
      setGroupError(e.message || "Failed to load option groups");
    } finally {
      setLoadingGroups(false);
    }
  }

  async function loadOptionsForGroup(groupId: number) {
    try {
      setLoadingOptions(true);
      setOptionError(null);
      const res = await fetch(
        `/admin/option-groups/${groupId}/options`,
        { credentials: "include" }
      );
      if (!res.ok) {
        throw new Error(`Failed to load options (${res.status})`);
      }
      const data = (await res.json()) as GameOptionRow[];
      setOptions(data);
    } catch (e: any) {
      setOptionError(e.message || "Failed to load options");
    } finally {
      setLoadingOptions(false);
    }
  }

  useEffect(() => {
    if (!selectedGameId) {
      setGroups([]);
      setOptions([]);
      setEditingGroupId(null);
      setOptionGroupId(null);
      return;
    }
    loadGroupsForGame(selectedGameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGameId]);

  useEffect(() => {
    if (optionGroupId == null) {
      setOptions([]);
      setEditingOptionId(null);
      return;
    }
    loadOptionsForGroup(optionGroupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionGroupId]);

  // ---- GROUP CRUD ----

  function resetGroupForm() {
    setEditingGroupId(null);
    setGroupKey("");
    setGroupLabel("");
    setGroupSortOrder("0");
    setGroupMultiSelect(false);
    setGroupIsRequired(true);
  }

  function hydrateGroupForm(row: GameOptionGroupRow) {
    setEditingGroupId(row.id);
    setGroupKey(row.key);
    setGroupLabel(row.label);
    setGroupSortOrder(String(row.sort_order ?? 0));
    setGroupMultiSelect(row.multi_select);
    setGroupIsRequired(row.is_required);
    setOptionGroupId(row.id);
  }

  async function handleGroupSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedGameId) {
      setGroupError("Select a game first");
      return;
    }
    setSavingGroup(true);
    setGroupError(null);
    try {
      const body = {
        key: groupKey.trim(),
        label: groupLabel.trim(),
        sort_order: Number(groupSortOrder) || 0,
        multi_select: groupMultiSelect,
        is_required: groupIsRequired,
      };

      const url = editingGroupId
        ? `/admin/option-groups/${editingGroupId}`
        : `/admin/games/${selectedGameId}/option-groups`;
      const method = editingGroupId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `Group save failed (${res.status})`);
      }

      await loadGroupsForGame(selectedGameId);

      if (!editingGroupId) {
        resetGroupForm();
      }
    } catch (e: any) {
      setGroupError(e.message || "Failed to save group");
    } finally {
      setSavingGroup(false);
    }
  }

  async function handleGroupDelete(id: number) {
    if (
      !window.confirm(
        "Delete this option group? This will also remove its options and any character links."
      )
    ) {
      return;
    }
    if (!selectedGameId) return;
    try {
      setSavingGroup(true);
      setGroupError(null);
      const res = await fetch(
        `/admin/option-groups/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (!res.ok && res.status !== 204) {
        throw new Error(`Group delete failed (${res.status})`);
      }
      await loadGroupsForGame(selectedGameId);
      if (editingGroupId === id) {
        resetGroupForm();
      }
      if (optionGroupId === id) {
        setOptionGroupId(null);
        setOptions([]);
      }
    } catch (e: any) {
      setGroupError(e.message || "Failed to delete group");
    } finally {
      setSavingGroup(false);
    }
  }

  // ---- OPTION CRUD ----

  function resetOptionForm() {
    setEditingOptionId(null);
    setOptionValueKey("");
    setOptionLabel("");
    setOptionSortOrder("0");
    setOptionIsActive(true);
    setOptionMetadata("");
    setOptionImageUrl(null);
    setImageError(null);
    // keep optionGroupId
  }

  function hydrateOptionForm(row: GameOptionRow) {
    setEditingOptionId(row.id);
    setOptionGroupId(row.group_id);
    setOptionValueKey(row.value_key);
    setOptionLabel(row.label);
    setOptionSortOrder(String(row.sort_order ?? 0));
    setOptionIsActive(row.is_active);
    setOptionMetadata(
      row.metadata && Object.keys(row.metadata).length > 0
        ? JSON.stringify(row.metadata, null, 2)
        : ""
    );
    setOptionImageUrl(row.image_url);
    setImageError(null);
  }

  async function handleOptionSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedGameId) {
      setOptionError("Select a game first");
      return;
    }
    if (optionGroupId == null) {
      setOptionError("Select an option group");
      return;
    }
    setSavingOption(true);
    setOptionError(null);
    try {
      let metadata: any = {};
      if (optionMetadata.trim()) {
        try {
          metadata = JSON.parse(optionMetadata);
        } catch {
          throw new Error("Metadata must be valid JSON");
        }
      }

      const body = {
        value_key: optionValueKey.trim(),
        label: optionLabel.trim(),
        sort_order: Number(optionSortOrder) || 0,
        is_active: optionIsActive,
        metadata,
      };

      const url = editingOptionId
        ? `/admin/options/${editingOptionId}`
        : `/admin/option-groups/${optionGroupId}/options`;
      const method = editingOptionId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `Option save failed (${res.status})`);
      }

      await loadOptionsForGroup(optionGroupId);

      if (!editingOptionId) {
        resetOptionForm();
      }
    } catch (e: any) {
      setOptionError(e.message || "Failed to save option");
    } finally {
      setSavingOption(false);
    }
  }

  async function handleOptionDeactivate(id: number) {
    if (!window.confirm("Deactivate this option for future selection?")) return;
    if (!selectedGameId) return;
    if (optionGroupId == null) return;
    try {
      setSavingOption(true);
      setOptionError(null);
      const res = await fetch(
        `/admin/options/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (!res.ok && res.status !== 204) {
        throw new Error(`Option deactivate failed (${res.status})`);
      }
      await loadOptionsForGroup(optionGroupId);
      if (editingOptionId === id) {
        resetOptionForm();
      }
    } catch (e: any) {
      setOptionError(e.message || "Failed to deactivate option");
    } finally {
      setSavingOption(false);
    }
  }

  // ---- OPTION IMAGE UPLOAD ----

  async function handleOptionImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Must have an option selected (need the ID for the upload)
    if (!editingOptionId) {
      setImageError("Save the option first before uploading an image");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setImageError("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setImageError("Image must be less than 5MB");
      return;
    }

    setUploadingImage(true);
    setImageError(null);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await fetch(`/admin/options/${editingOptionId}/image`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Upload failed");
      }

      const data = await res.json();
      setOptionImageUrl(data.image_url);

      // Refresh the options list to show the new image
      if (optionGroupId != null) {
        await loadOptionsForGroup(optionGroupId);
      }
    } catch (err: any) {
      setImageError(err.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
      // Reset the input so the same file can be selected again
      e.target.value = "";
    }
  }

  async function handleOptionImageDelete() {
    if (!editingOptionId) return;
    if (!window.confirm("Remove this image?")) return;

    setUploadingImage(true);
    setImageError(null);

    try {
      const res = await fetch(`/admin/options/${editingOptionId}/image`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error("Failed to remove image");
      }

      setOptionImageUrl(null);

      // Refresh the options list
      if (optionGroupId != null) {
        await loadOptionsForGroup(optionGroupId);
      }
    } catch (err: any) {
      setImageError(err.message || "Failed to remove image");
    } finally {
      setUploadingImage(false);
    }
  }

  const optionsForCurrentGroup = useMemo(
    () =>
      optionGroupId == null
        ? []
        : options,
    [options, optionGroupId]
  );

  return (
    <>
      {/* PAGE HEADER */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-[var(--c-ink-2)] mb-0.5">
            <span className="px-1.5 py-0.5 rounded-full border border-cyan-300/40 bg-cyan-300/10 text-cyan-800 dark:text-cyan-100">
              Admin
            </span>
            <span className="px-1.5 py-0.5 rounded-full border border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-2)]">
              Game configuration
            </span>
          </div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            Manage games, option groups &amp; options
            <span className="h-[1px] flex-1 bg-gradient-to-r from-cyan-300/60 via-[var(--c-sunken-2)] to-transparent" />
          </h2>
          <p className="text-xs text-[var(--c-ink-2)]">
            Games are your roots. Option groups and options define things like
            region, faction, roles, raids, and anything else that should be structured.
          </p>
        </div>
      </div>

      {/* GAMES SECTION */}
      <div className="grid gap-4 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] mb-6">
        {/* List */}
        <div className="overflow-hidden rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] backdrop-blur-lg">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--c-line)] text-xs text-[var(--c-ink-2)]">
            <span>Games</span>
            <button
              onClick={loadGames}
              className="rounded border border-[var(--c-line-strong)] bg-[var(--c-sunken)] px-2 py-1 hover:bg-[var(--c-sunken-2)]"
            >
              Reload
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[var(--c-sunken)] text-xs uppercase tracking-wide text-[var(--c-ink-2)]">
              <tr>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left hidden md:table-cell">
                  Active
                </th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {games.map((g) => {
                const isSelected = g.id === selectedGameId;
                return (
                  <tr
                    key={g.id}
                    className={`cursor-pointer hover:bg-[var(--c-sunken)] ${
                      isSelected ? "bg-cyan-400/10" : ""
                    }`}
                    onClick={() => {
                      hydrateGameForm(g);
                    }}
                  >
                    <td className="px-3 py-2 text-xs font-mono">{g.code}</td>
                    <td className="px-3 py-2 text-xs">{g.name}</td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell">
                      {g.is_active ? "Yes" : "No"}
                    </td>
                    <td
                      className="px-3 py-2 text-xs text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => hydrateGameForm(g)}
                        className="mr-2 text-cyan-700 dark:text-cyan-300 hover:text-cyan-800 dark:hover:text-cyan-100"
                      >
                        Edit
                      </button>
                      {g.is_active && (
                        <button
                          onClick={() => handleGameSoftDelete(g.id)}
                          className="text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-100"
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {games.length === 0 && !loadingGames && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-4 text-center text-xs text-[var(--c-ink-2)]"
                  >
                    No games yet. Use the form on the right to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {loadingGames && (
            <div className="px-3 py-2 text-xs text-[var(--c-ink-2)] border-t border-[var(--c-line)]">
              Loading games...
            </div>
          )}
        </div>

        {/* Game form */}
        <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] backdrop-blur-lg px-4 py-3 text-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-[var(--c-ink)]">
              {editingGameId ? "Edit game" : "Add game"}
            </div>
            {editingGameId && (
              <button
                type="button"
                onClick={resetGameForm}
                className="text-[11px] text-[var(--c-ink-2)] hover:text-[var(--c-ink)]"
              >
                New
              </button>
            )}
          </div>

          <form onSubmit={handleGameSubmit} className="space-y-3 text-xs">
            <Field label="Code (unique, stable identifier)">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1.5 text-xs outline-none focus:border-cyan-400"
                placeholder="wow_classic_anniversary"
                required
              />
            </Field>
            <Field label="Display name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1.5 text-xs outline-none focus:border-cyan-400"
                placeholder="WoW Classic Anniversary"
                required
              />
            </Field>
            <Field label="Image URL (for now)">
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="w-full rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1.5 text-xs outline-none focus:border-cyan-400"
                placeholder="https://..."
              />
            </Field>
            <Field label="Active">
              <label className="inline-flex items-center gap-2 text-[11px]">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-3 w-3 rounded border border-[var(--c-ink-4)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)]"
                />
                <span>Game visible in selectors</span>
              </label>
            </Field>

            {gameError && (
              <div className="text-[11px] text-red-700 dark:text-red-300 bg-red-500/10 dark:bg-red-900/40 border border-red-500/40 rounded px-2 py-1">
                {gameError}
              </div>
            )}

            <div className="pt-1 flex justify-end gap-2">
              <button
                type="button"
                onClick={resetGameForm}
                className="rounded-md border border-[var(--c-line-strong)] bg-[var(--c-sunken)] px-3 py-1.5 text-[11px] hover:bg-[var(--c-sunken-2)]"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={savingGame}
                className="rounded-md border border-cyan-400/60 bg-cyan-400/20 px-4 py-1.5 text-[11px] font-semibold hover:bg-cyan-400/30 disabled:opacity-60"
              >
                {savingGame
                  ? editingGameId
                    ? "Saving..."
                    : "Creating..."
                  : editingGameId
                  ? "Save changes"
                  : "Create game"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* GROUPS + OPTIONS */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1.3fr)]">
        {/* GROUPS */}
        <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] backdrop-blur-lg px-4 py-3 text-sm">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-[var(--c-ink-2)] mb-0.5">
                <span className="px-1.5 py-0.5 rounded-full border border-emerald-300/40 bg-emerald-300/10 text-emerald-800 dark:text-emerald-100">
                  Option groups
                </span>
                {selectedGame && (
                  <span className="px-1.5 py-0.5 rounded-full border border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-2)]">
                    {selectedGame.name}
                  </span>
                )}
              </div>
              <h3 className="text-sm font-semibold">
                Dimensions for this game
              </h3>
            </div>
            {selectedGame && (
              <button
                type="button"
                onClick={resetGroupForm}
                className="text-[11px] text-[var(--c-ink-2)] hover:text-[var(--c-ink)]"
              >
                New group
              </button>
            )}
          </div>

          {!selectedGame && (
            <div className="text-xs text-[var(--c-ink-2)]">
              Select a game from the list above to manage its option groups.
            </div>
          )}

          {selectedGame && (
            <>
              <div className="max-h-52 overflow-auto rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] mb-3">
                <table className="w-full text-xs">
                  <thead className="bg-[var(--c-sunken)] text-[10px] uppercase tracking-wide text-[var(--c-ink-2)]">
                    <tr>
                      <th className="px-2 py-1 text-left">Key</th>
                      <th className="px-2 py-1 text-left">Label</th>
                      <th className="px-2 py-1 text-right">Sort</th>
                      <th className="px-2 py-1 text-center">Multi</th>
                      <th className="px-2 py-1 text-center">Required</th>
                      <th className="px-2 py-1 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => {
                      const isCurrent = optionGroupId === g.id;
                      return (
                        <tr
                          key={g.id}
                          className={`cursor-pointer hover:bg-[var(--c-sunken)] ${
                            isCurrent ? "bg-emerald-400/10" : ""
                          }`}
                          onClick={() => {
                            hydrateGroupForm(g);
                          }}
                        >
                          <td className="px-2 py-1 font-mono">{g.key}</td>
                          <td className="px-2 py-1">{g.label}</td>
                          <td className="px-2 py-1 text-right">
                            {g.sort_order}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {g.multi_select ? "✓" : ""}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {g.is_required ? "✓" : ""}
                          </td>
                          <td
                            className="px-2 py-1 text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => hydrateGroupForm(g)}
                              className="mr-2 text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleGroupDelete(g.id)}
                              className="text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-100"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {groups.length === 0 && !loadingGroups && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-2 py-3 text-center text-[11px] text-[var(--c-ink-2)]"
                        >
                          No groups yet. Create things like region, faction,
                          class, role, raid, etc. here.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {loadingGroups && (
                  <div className="px-2 py-1 text-[11px] text-[var(--c-ink-2)] border-t border-[var(--c-line)]">
                    Loading groups...
                  </div>
                )}
              </div>

              <form
                onSubmit={handleGroupSubmit}
                className="space-y-2 text-xs mt-2"
              >
                <Field label="Group key (stable identifier)">
                  <input
                    value={groupKey}
                    onChange={(e) => setGroupKey(e.target.value)}
                    className="w-full rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1.5 text-xs outline-none focus:border-emerald-400"
                    placeholder="region, faction, primary_role"
                    required
                  />
                </Field>
                <Field label="Label">
                  <input
                    value={groupLabel}
                    onChange={(e) => setGroupLabel(e.target.value)}
                    className="w-full rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1.5 text-xs outline-none focus:border-emerald-400"
                    placeholder="Region"
                    required
                  />
                </Field>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Sort order">
                    <input
                      type="number"
                      value={groupSortOrder}
                      onChange={(e) => setGroupSortOrder(e.target.value)}
                      className="w-full rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1.5 text-xs outline-none focus:border-emerald-400"
                    />
                  </Field>
                  <Field label="Multi-select">
                    <label className="inline-flex items-center gap-2 text-[11px] mt-1">
                      <input
                        type="checkbox"
                        checked={groupMultiSelect}
                        onChange={(e) => setGroupMultiSelect(e.target.checked)}
                        className="h-3 w-3 rounded border border-[var(--c-ink-4)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)]"
                      />
                      <span>Allow multiple options</span>
                    </label>
                  </Field>
                  <Field label="Required">
                    <label className="inline-flex items-center gap-2 text-[11px] mt-1">
                      <input
                        type="checkbox"
                        checked={groupIsRequired}
                        onChange={(e) =>
                          setGroupIsRequired(e.target.checked)
                        }
                        className="h-3 w-3 rounded border border-[var(--c-ink-4)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)]"
                      />
                      <span>Must have a value</span>
                    </label>
                  </Field>
                </div>

                {groupError && (
                  <div className="text-[11px] text-red-700 dark:text-red-300 bg-red-500/10 dark:bg-red-900/40 border border-red-500/40 rounded px-2 py-1">
                    {groupError}
                  </div>
                )}

                <div className="pt-1 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={resetGroupForm}
                    className="rounded-md border border-[var(--c-line-strong)] bg-[var(--c-sunken)] px-3 py-1.5 text-[11px] hover:bg-[var(--c-sunken-2)]"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={savingGroup}
                    className="rounded-md border border-emerald-400/60 bg-emerald-400/20 px-4 py-1.5 text-[11px] font-semibold hover:bg-emerald-400/30 disabled:opacity-60"
                  >
                    {savingGroup
                      ? editingGroupId
                        ? "Saving..."
                        : "Creating..."
                      : editingGroupId
                      ? "Save group"
                      : "Create group"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        {/* OPTIONS */}
        <div className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] backdrop-blur-lg px-4 py-3 text-sm">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-[var(--c-ink-2)] mb-0.5">
                <span className="px-1.5 py-0.5 rounded-full border border-cyan-300/40 bg-cyan-300/10 text-cyan-800 dark:text-cyan-100">
                  Options
                </span>
                {selectedGroup && (
                  <span className="px-1.5 py-0.5 rounded-full border border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-2)]">
                    {selectedGroup.label}
                  </span>
                )}
              </div>
              <h3 className="text-sm font-semibold">
                Values for a specific group
              </h3>
            </div>
            {selectedGroup && (
              <button
                type="button"
                onClick={resetOptionForm}
                className="text-[11px] text-[var(--c-ink-2)] hover:text-[var(--c-ink)]"
              >
                New option
              </button>
            )}
          </div>

          {!selectedGame && (
            <div className="text-xs text-[var(--c-ink-2)]">
              Select a game first, then a group, to manage options.
            </div>
          )}

          {selectedGame && !groups.length && !loadingGroups && (
            <div className="text-xs text-[var(--c-ink-2)]">
              Create at least one option group on the left to attach options to.
            </div>
          )}

          {selectedGame && groups.length > 0 && (
            <>
              <div className="flex items-center gap-2 mb-2 text-xs">
                <span className="text-[var(--c-ink-2)]">Group:</span>
                <select
                  value={optionGroupId ?? ""}
                  onChange={(e) =>
                    setOptionGroupId(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                  className="rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1 text-xs outline-none focus:border-cyan-400"
                >
                  <option value="">Select group...</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.label} ({g.key})
                    </option>
                  ))}
                </select>
              </div>

              <div className="max-h-52 overflow-auto rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] mb-3">
                <table className="w-full text-xs">
                  <thead className="bg-[var(--c-sunken)] text-[10px] uppercase tracking-wide text-[var(--c-ink-2)]">
                    <tr>
                      <th className="px-2 py-1 text-left">Key</th>
                      <th className="px-2 py-1 text-left">Label</th>
                      <th className="px-2 py-1 text-center">Img</th>
                      <th className="px-2 py-1 text-right">Sort</th>
                      <th className="px-2 py-1 text-center">Active</th>
                      <th className="px-2 py-1 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {optionGroupId != null &&
                      optionsForCurrentGroup.map((o) => (
                        <tr
                          key={o.id}
                          className="cursor-pointer hover:bg-[var(--c-sunken)]"
                          onClick={() => hydrateOptionForm(o)}
                        >
                          <td className="px-2 py-1 font-mono">
                            {o.value_key}
                          </td>
                          <td className="px-2 py-1">{o.label}</td>
                          <td className="px-2 py-1 text-center">
                            {o.image_url ? (
                              <img 
                                src={o.image_url} 
                                alt="" 
                                className="w-6 h-6 rounded object-cover inline-block"
                              />
                            ) : (
                              <span className="text-[var(--c-ink-4)]">-</span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {o.sort_order}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {o.is_active ? "✓" : ""}
                          </td>
                          <td
                            className="px-2 py-1 text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => hydrateOptionForm(o)}
                              className="mr-2 text-cyan-700 dark:text-cyan-300 hover:text-cyan-800 dark:hover:text-cyan-100"
                            >
                              Edit
                            </button>
                            {o.is_active && (
                              <button
                                onClick={() => handleOptionDeactivate(o.id)}
                                className="text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-100"
                              >
                                Deactivate
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    {optionGroupId != null &&
                      !optionsForCurrentGroup.length &&
                      !loadingOptions && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-2 py-3 text-center text-[11px] text-[var(--c-ink-2)]"
                          >
                            No options yet for this group.
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
                {loadingOptions && (
                  <div className="px-2 py-1 text-[11px] text-[var(--c-ink-2)] border-t border-[var(--c-line)]">
                    Loading options...
                  </div>
                )}
              </div>

              {selectedGroup && (
                <form
                  onSubmit={handleOptionSubmit}
                  className="space-y-2 text-xs mt-2"
                >
                  <Field label="Value key (stable identifier)">
                    <input
                      value={optionValueKey}
                      onChange={(e) => setOptionValueKey(e.target.value)}
                      className="w-full rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1.5 text-xs outline-none focus:border-cyan-400"
                      placeholder="eu, alliance, tank"
                      required
                    />
                  </Field>
                  <Field label="Label">
                    <input
                      value={optionLabel}
                      onChange={(e) => setOptionLabel(e.target.value)}
                      className="w-full rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1.5 text-xs outline-none focus:border-cyan-400"
                      placeholder="EU"
                      required
                    />
                  </Field>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Sort order">
                      <input
                        type="number"
                        value={optionSortOrder}
                        onChange={(e) => setOptionSortOrder(e.target.value)}
                        className="w-full rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1.5 text-xs outline-none focus:border-cyan-400"
                      />
                    </Field>
                    <Field label="Active">
                      <label className="inline-flex items-center gap-2 text-[11px] mt-1">
                        <input
                          type="checkbox"
                          checked={optionIsActive}
                          onChange={(e) =>
                            setOptionIsActive(e.target.checked)
                          }
                          className="h-3 w-3 rounded border border-[var(--c-ink-4)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)]"
                        />
                        <span>Visible in selectors</span>
                      </label>
                    </Field>
                    <Field label="Metadata (optional)">
                      <div className="mt-1 flex items-start justify-between gap-2">
                        <span className="text-[10px] text-[var(--c-ink-2)]">
                          Toggle config or custom JSON (colours, icons, etc.)
                        </span>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              setOptionMetadata(
                                JSON.stringify(
                                  {
                                    ui_component: "toggle",
                                    show_on_character_form: true,
                                  },
                                  null,
                                  2
                                )
                              )
                            }
                            className="rounded border border-cyan-400/60 bg-cyan-400/10 px-2 py-0.5 text-[10px] text-cyan-800 dark:text-cyan-100 hover:bg-cyan-400/20"
                          >
                            Toggle
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setOptionMetadata(
                                JSON.stringify(
                                  {
                                    show_on_character_form: true,
                                    allowed_class_keys: ["warrior", "paladin"],
                                  },
                                  null,
                                  2
                                )
                              )
                            }
                            className="rounded border border-emerald-400/60 bg-emerald-400/10 px-2 py-0.5 text-[10px] text-emerald-800 dark:text-emerald-100 hover:bg-emerald-400/20"
                          >
                            Role&nbsp;restrict
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setOptionMetadata(
                                JSON.stringify(
                                  {
                                    size: 10,
                                    duration: 180,
                                    composition: {
                                      tanks: { min: 2, max: 2 },
                                      healers: { min: 2, max: 3 }
                                    }
                                  },
                                  null,
                                  2
                                )
                              )
                            }
                            className="rounded border border-amber-400/60 bg-amber-400/10 px-2 py-0.5 text-[10px] text-amber-800 dark:text-amber-100 hover:bg-amber-400/20"
                          >
                            Raid&nbsp;template
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setOptionMetadata(
                                JSON.stringify(
                                  {
                                    allowed_region_keys: ["eu"]
                                  },
                                  null,
                                  2
                                )
                              )
                            }
                            className="rounded border border-violet-400/60 bg-violet-400/10 px-2 py-0.5 text-[10px] text-violet-800 dark:text-violet-100 hover:bg-violet-400/20"
                          >
                            Realm
                          </button>
                        </div>
                      </div>
                    </Field>
                  </div>
                  <textarea
                    value={optionMetadata}
                    onChange={(e) => setOptionMetadata(e.target.value)}
                    className="w-full rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-2 py-1.5 text-[11px] outline-none focus:border-cyan-400 font-mono min-h-[72px]"
                    placeholder='{"color":"#00FFFF"}'
                  />

                  {/* Image Upload Section */}
                  <div className="mt-3 p-3 rounded-lg border border-[var(--c-line)] bg-[var(--c-bg-veil)]">
                    <div className="text-[11px] font-semibold text-[var(--c-ink-2)] mb-2">
                      Option Image
                    </div>
                    
                    {optionImageUrl ? (
                      <div className="flex items-start gap-3">
                        <img 
                          src={optionImageUrl} 
                          alt="Option" 
                          className="w-20 h-20 rounded-lg object-cover border border-[var(--c-line)]"
                        />
                        <div className="flex-1">
                          <p className="text-[10px] text-[var(--c-ink-3)] mb-2 break-all">
                            {optionImageUrl.split('/').pop()}
                          </p>
                          <button
                            type="button"
                            onClick={handleOptionImageDelete}
                            disabled={uploadingImage}
                            className="text-[10px] text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50"
                          >
                            {uploadingImage ? "Removing..." : "Remove image"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {editingOptionId ? (
                          <label className="flex items-center justify-center w-full h-20 rounded-lg border border-dashed border-[var(--c-line-strong)] cursor-pointer hover:border-[var(--c-ink-4)] hover:bg-[var(--c-bg-veil)] transition-all">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleOptionImageUpload}
                              disabled={uploadingImage}
                              className="hidden"
                            />
                            <div className="text-center">
                              {uploadingImage ? (
                                <span className="text-[11px] text-[var(--c-ink-3)]">Uploading...</span>
                              ) : (
                                <>
                                  <div className="text-[var(--c-ink-4)] text-lg mb-1">+</div>
                                  <span className="text-[10px] text-[var(--c-ink-3)]">Click to upload</span>
                                </>
                              )}
                            </div>
                          </label>
                        ) : (
                          <div className="text-[10px] text-[var(--c-ink-3)] italic">
                            Save the option first to enable image upload
                          </div>
                        )}
                      </div>
                    )}
                    
                    {imageError && (
                      <div className="mt-2 text-[10px] text-red-600 dark:text-red-400">
                        {imageError}
                      </div>
                    )}
                  </div>

                  {optionError && (
                    <div className="text-[11px] text-red-700 dark:text-red-300 bg-red-500/10 dark:bg-red-900/40 border border-red-500/40 rounded px-2 py-1">
                      {optionError}
                    </div>
                  )}

                  <div className="pt-1 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={resetOptionForm}
                      className="rounded-md border border-[var(--c-line-strong)] bg-[var(--c-sunken)] px-3 py-1.5 text-[11px] hover:bg-[var(--c-sunken-2)]"
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      disabled={savingOption}
                      className="rounded-md border border-cyan-400/60 bg-cyan-400/20 px-4 py-1.5 text-[11px] font-semibold hover:bg-cyan-400/30 disabled:opacity-60"
                    >
                      {savingOption
                        ? editingOptionId
                          ? "Saving..."
                          : "Creating..."
                        : editingOptionId
                        ? "Save option"
                        : "Create option"}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}